import { useEffect, useState } from "react";
import {
    Container,
    Box,
    CircularProgress,
    useMediaQuery,
    ThemeProvider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import NavBar from "../../layout/NavBar";
import { darkTheme } from "../../../theme/darkTheme";
import { GewichtApi } from "../../../services/api";
import { useAuth } from "../../context/AuthContext";
import GewichtStatistik from '../../shared/GewichtStatistik';
import GewichtWochenVerlauf from '../../shared/components/GewichtWochenVerlauf';
import NavBarBot from "../../layout/NavBarBot";
import LoadingNavBarBot from '../../layout/LoadingNavBarBot';
import BackButton from "../../util/buttons/BackButton";
import HeaderCard from "../../layout/HeaderCard";
import Notification from "../../util/notifications/Notification";
import GewichtAddDialog from "./components/gewichttracking/GewichtAddDialog";
import GewichtEditDialog from "./components/gewichttracking/GewichtEditDialog";
import GewichtDeleteDialog from "./components/gewichttracking/GewichtDeleteDialog";

function GewichtTrackingPage() {
    const [message, setMessage] = useState({ type: "", text: "" });
    const [, setShowNotification] = useState(false);
    const isMobile = useMediaQuery(darkTheme.breakpoints.down("md"));
    const { nutzer } = useAuth();

    const [loading, setLoading] = useState(false);
    const [gewichtData, setGewichtData] = useState([]);
    const [, setStats] = useState(null);

    const [formData, setFormData] = useState({
        gewicht: "",
        datum: new Date().toISOString().split("T")[0],
        notiz: "",
    });

    const [editDialog, setEditDialog] = useState({ open: false, entry: null });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
    const [addDialogOpen, setAddDialogOpen] = useState(false);

    useEffect(() => {
        if (nutzer?.id) {
            fetchGewichtData();
            fetchStats();
        }
    }, [nutzer]);

    const fetchGewichtData = async () => {
        try {
            setLoading(true);
            const response = await GewichtApi.getGewichtByNutzer(nutzer.id, { limit: 30 });
            setGewichtData(response.data);
        } catch (err) {
            console.error(err);
            setMessage({ type: "error", text: 'Fehler beim Laden der Daten' });
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await GewichtApi.getErweiterteStats(nutzer.id, { days: 30 });
            setStats(response.data);
        } catch (err) {
            console.error(err);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        if (!formData.gewicht || !formData.datum) {
            setMessage({ type: "error", text: 'Gewicht und Datum sind erforderlich' });

            return;
        }

        try {
            setLoading(true);
            await GewichtApi.createGewicht({
                nutzer_id: nutzer.id,
                gewicht: parseFloat(formData.gewicht),
                datum: formData.datum,
                notiz: formData.notiz,
            });
            setMessage({ type: "success", text: 'Gewicht erfolgreich eingetragen' });
            setFormData({
                gewicht: "",
                datum: new Date().toISOString().split("T")[0],
                notiz: "",
            });
            fetchGewichtData();
            fetchStats();
        } catch (err) {
            console.error(err);
            setMessage({ type: "error", text: err.response?.data?.error || "Fehler beim Speichern" });
        } finally {
            setLoading(false);
            // window.location.reload();
        }
    };

    const handleUpdate = async () => {
        try {
            setLoading(true);
            await GewichtApi.updateGewicht(editDialog.entry.id, {
                gewicht: parseFloat(editDialog.entry.gewicht),
                datum: editDialog.entry.datum,
                notiz: editDialog.entry.notiz,
            });
            setMessage({ type: "success", text: 'Gewicht aktualisiert' });
            setEditDialog({ open: false, entry: null });
            fetchGewichtData();
            fetchStats();
        } catch (err) {
            console.error(err);
            setMessage({ type: "error", text: 'Fehler beim Aktualisieren' });
        } finally {
            setLoading(false);
            window.location.reload();
        }
    };

    const handleDelete = async () => {
        try {
            setLoading(true);
            await GewichtApi.deleteGewicht(deleteDialog.id);

            setMessage({ type: "success", text: 'Eintrag gelöscht' });
            setDeleteDialog({ open: false, id: null });
            fetchGewichtData();
            fetchStats();
        } catch (err) {
            console.error(err);
            setMessage({ type: "error", text: 'Fehler beim Löschen' });

        } finally {
            setLoading(false);
        }
    };

    if (loading && !gewichtData.length) {
        return (
            <>
                <ThemeProvider theme={darkTheme}>
                    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
                        <NavBar />
                        <CircularProgress sx={{ position: "absolute", top: "45%", left: "45%", display: 'block', mx: 'auto', mb: 2 }} />
                    </Box>
                </ThemeProvider>
                <LoadingNavBarBot />
            </>
        );
    }

    return (
        <>
            <ThemeProvider theme={darkTheme}>
                <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: "54px" }}>
                    <NavBar />
                    <Container maxWidth="md" sx={{ pt: { xs: 2, md: 4 } }}>
                        {!isMobile && (
                            <BackButton />
                        )}
                        <HeaderCard title={"Gewicht Tracker"} />

                        {message.status === "error" || message.status === "success" && (
                            <Notification
                                type={message.type}
                                message={message.text}
                                onClose={() => {
                                    setShowNotification(true);
                                    setMessage({ type: "", text: "" });
                                }}
                            />
                        )}
                    </Container>

                    {/* Add Dialog (replaces inline 'Gewicht eintragen' card) */}
                    <GewichtAddDialog
                        open={addDialogOpen}
                        onClose={() => setAddDialogOpen(false)}
                        formData={formData}
                        onInputChange={handleInputChange}
                        loading={loading}
                        onSubmit={async () => {
                            await handleSubmit();
                            setAddDialogOpen(false);
                        }}
                    />

                    {/* Edit Dialog */}
                    <GewichtEditDialog
                        editDialog={editDialog}
                        onChange={setEditDialog}
                        loading={loading}
                        onUpdate={handleUpdate}
                        onClose={() => setEditDialog({ open: false, entry: null })}
                    />

                    {/* Delete Dialog */}
                    <GewichtDeleteDialog
                        open={deleteDialog.open}
                        onClose={() => setDeleteDialog({ open: false, id: null })}
                        loading={loading}
                        onDelete={handleDelete}
                    />

                    {/* Erweiterte Statistik & Kalorien-Analyse */}
                    <Container maxWidth="lg" sx={{
                        paddingTop: "8px",
                        paddingLeft: "16px",
                        paddingRight: "16px",
                        minHeight: '400px'  // Mindesthöhe hinzufügen
                    }}>
                        <GewichtStatistik
                            nutzerId={nutzer?.id}
                            compact={false}
                            onError={(error) => setMessage({ type: "error", text: error })}
                        />

                        <GewichtWochenVerlauf
                            nutzerId={nutzer?.id}
                        />

                    </Container>
                </Box>
            </ThemeProvider>
            <NavBarBot mainBtnF={() => setAddDialogOpen(true)} mainBtnTxt={<AddIcon />} />
        </>
    );
}

export default GewichtTrackingPage;
